import { type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useIntl } from 'react-intl'

import { cn } from '@gto/ui/cn'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAdminPartnerDetailSchema } from '@/api/gen'
import { useListPayouts } from '@/api/gen'
import { usePartnerCouponCount } from '@/api/partner-coupon-hooks'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import type { CouponListSearch, PartnerDetailTab } from '@/utils/partner-detail-search-params'

import { CampaignsTab } from './tabs/campaigns-tab'
import { CommissionsTab } from './tabs/commissions-tab'
import { CouponsTab } from './tabs/coupons-tab'
import { OverviewTab } from './tabs/overview-tab'
import { PayoutsTab } from './tabs/payouts-tab'
import { ReportTab } from './tabs/report-tab'

type TabValue = PartnerDetailTab

type MainPanelProps = {
  partner: AffiliateAdminPartnerDetailSchema
  partnerId: string
  tab: TabValue
  /** The Links tab's view (tag, status, batch, search, sort, page); lives in the
   *  URL, so the page owns it. */
  couponSearch: CouponListSearch
  onTabChange: (tab: TabValue) => void
  onCouponSearchChange: (delta: Partial<CouponListSearch>, opts?: { resetPage?: boolean }) => void
}

export const MainPanel = ({
  partner,
  partnerId,
  tab,
  couponSearch,
  onTabChange,
  onCouponSearchChange,
}: MainPanelProps) => {
  // Best-effort badge counts. The user-facing surface is just a count next to
  // the tab label; an inline alert here would be noisy. But silent
  // observability is not — route both through useReportQueryError so a
  // regression that breaks the counts reaches Sentry. The tab-body queries
  // each have their own InlineQueryError on failure.
  //
  // Deliberately UNFILTERED: the badge counts every link the partner has, so it
  // must not shrink when the tab scopes itself to a tag, a batch or a search.
  const couponsBadgeQuery = usePartnerCouponCount(partnerId)
  const payoutsBadgeQuery = useListPayouts({ partner_id: partnerId })
  const { data: couponCount } = couponsBadgeQuery
  const { data: payouts } = payoutsBadgeQuery
  useReportQueryError(couponsBadgeQuery, 'usePartnerCouponCount (MainPanel badge) failed: ')
  useReportQueryError(payoutsBadgeQuery, 'useListPayouts (MainPanel badge) failed: ')

  const tabs: { value: TabValue; labelId: string; count?: number }[] = [
    { value: 'overview', labelId: 'admin.partnerDetail.tab.overview' },
    { value: 'report', labelId: 'admin.partnerDetail.tab.report' },
    { value: 'coupons', labelId: 'admin.partnerDetail.tab.coupons', count: couponCount },
    { value: 'campaigns', labelId: 'admin.partnerDetail.tab.campaigns' },
    {
      value: 'commissions',
      labelId: 'admin.partnerDetail.tab.commissions',
      count: partner.total_orders,
    },
    { value: 'payouts', labelId: 'admin.partnerDetail.tab.payouts', count: payouts?.length },
  ]

  return (
    // `min-w-0`: this panel is the flexible column of the partner-detail grid and
    // holds the Links table, whose min-content width exceeds that column on a
    // laptop. A grid item's automatic minimum is its min-content size, so without
    // this the panel refuses to shrink and overflows into the shell's clip region
    // — see the grid's own note on partner-detail-page.
    <div className="flex min-w-0 flex-col gap-3">
      <TabBar
        tabs={tabs}
        value={tab}
        onChange={onTabChange}
      />

      {tab === 'overview' && (
        <OverviewTab
          partner={partner}
          partnerId={partnerId}
        />
      )}
      {tab === 'report' && <ReportTab partnerId={partnerId} />}
      {tab === 'coupons' && (
        <CouponsTab
          partnerId={partnerId}
          search={couponSearch}
          onSearchChange={onCouponSearchChange}
        />
      )}
      {tab === 'campaigns' && <CampaignsTab partnerId={partnerId} />}
      {tab === 'commissions' && <CommissionsTab partnerId={partnerId} />}
      {tab === 'payouts' && <PayoutsTab partnerId={partnerId} />}
    </div>
  )
}

type TabBarProps = {
  tabs: { value: TabValue; labelId: string; count?: number }[]
  value: TabValue
  onChange: (tab: TabValue) => void
}

const TabBar = ({ tabs, value, onChange }: TabBarProps) => {
  const { formatMessage } = useIntl()

  // APG tabs pattern: Left/Right cycle through tabs, Home/End jump to the
  // edges, and selection follows focus (automatic activation — switching is a
  // cheap URL replace). Roving tabindex below keeps a single tab stop.
  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const currentIndex = tabs.findIndex((t) => t.value === value)
    const lastIndex = tabs.length - 1
    let nextIndex: number | null = null
    if (e.key === 'ArrowRight') {
      nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1
    } else if (e.key === 'ArrowLeft') {
      nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1
    } else if (e.key === 'Home') {
      nextIndex = 0
    } else if (e.key === 'End') {
      nextIndex = lastIndex
    }
    const next = nextIndex == null ? undefined : tabs[nextIndex]
    if (next == null) {
      return
    }
    e.preventDefault()
    onChange(next.value)
    e.currentTarget.querySelector<HTMLButtonElement>(`[data-tst="tab-${next.value}"]`)?.focus()
  }

  return (
    <div
      className="flex items-center gap-2 border-b border-divider"
      role="tablist"
      onKeyDown={handleKeyDown}
    >
      {tabs.map((t) => {
        const active = t.value === value

        return (
          <button
            key={t.value}
            aria-selected={active}
            data-tst={`tab-${t.value}`}
            role="tab"
            tabIndex={active ? 0 : -1}
            type="button"
            className={cn(
              'inline-flex items-center gap-1 border-b-2 border-transparent bg-transparent px-3 py-2 text-text-secondary',
              active && 'border-primary-main text-text-primary',
            )}
            onClick={() => onChange(t.value)}
          >
            <Typography
              className="font-medium"
              color="inherit"
              variant="body2"
            >
              {formatMessage({ id: t.labelId })}
            </Typography>
            {t.count != null && (
              <Typography
                className="font-mono tabular-nums"
                color="text.secondary"
                variant="caption"
              >
                {t.count}
              </Typography>
            )}
          </button>
        )
      })}
    </div>
  )
}
